import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PlusCircle, Pencil, Trash2 } from 'lucide-react';
import api, { formatApiErrorDetail } from '@/lib/adminApi';

const emptyForm = (config) => {
  const f = { ...config.defaults };
  config.fields.forEach((field) => {
    if (f[field.name] !== undefined) return;
    if (field.type === 'checkbox') f[field.name] = false;
    else if (field.type === 'number') f[field.name] = 0;
    else f[field.name] = '';
  });
  return f;
};

const toForm = (config, item) => {
  const f = { ...config.defaults, ...item };
  config.fields.forEach((field) => {
    if (field.type === 'csv' && Array.isArray(f[field.name])) f[field.name] = f[field.name].join(', ');
  });
  return f;
};

const toPayload = (config, form) => {
  const p = { ...form };
  config.fields.forEach((field) => {
    if (field.type === 'csv') {
      p[field.name] = String(p[field.name] || '').split(',').map((t) => t.trim()).filter(Boolean);
    } else if (field.type === 'number') {
      p[field.name] = field.float ? parseFloat(p[field.name]) || 0 : parseInt(p[field.name], 10) || 0;
    }
  });
  return p;
};

export const ResourceManager = ({ config }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const { data } = await api.get(`${config.endpoint}${config.listQuery}`);
      setItems(data);
    } catch {
      toast.error(`Failed to load ${config.label.toLowerCase()}`);
    }
  }, [config]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm(config)); setOpen(true); };
  const openEdit = (item) => { setEditingId(item[config.idField]); setForm(toForm(config, item)); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(config, form);
      if (editingId) {
        await api.put(`${config.endpoint}/${editingId}`, payload);
        toast.success(`${config.label.slice(0, -1)} updated`);
      } else {
        await api.post(config.endpoint, payload);
        toast.success(`${config.label.slice(0, -1)} created`);
      }
      setOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item[config.columns[0].name]}"? This cannot be undone.`)) return;
    try {
      await api.delete(`${config.endpoint}/${item[config.idField]}`);
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="border border-border/40 rounded-sm bg-card" data-testid={`${config.key}-manager`}>
      <div className="flex items-center justify-between p-4 border-b border-border/40">
        <h2 className="text-xl font-playfair font-bold">{config.label} ({items.length})</h2>
        <Button onClick={openCreate} className="rounded-sm" data-testid={`${config.key}-add-btn`}>
          <PlusCircle className="mr-2" size={16} /> Add New
        </Button>
      </div>

      <div className="divide-y divide-border/40">
        {items.length === 0 && (
          <p className="p-6 text-muted-foreground text-sm">No {config.label.toLowerCase()} yet.</p>
        )}
        {items.map((item) => (
          <div key={item[config.idField]} className="flex items-center gap-4 p-4" data-testid={`${config.key}-row`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{String(item[config.columns[0].name])}</p>
              <p className="text-sm text-muted-foreground truncate">
                {config.columns.slice(1).map((c) => `${c.label}: ${String(item[c.name])}`).join('  ·  ')}
              </p>
            </div>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openEdit(item)} data-testid={`${config.key}-edit-btn`}>
              <Pencil size={14} />
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm text-red-600 hover:text-red-700" onClick={() => remove(item)} data-testid={`${config.key}-delete-btn`}>
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair">
              {editingId ? `Edit ${config.label.slice(0, -1)}` : `New ${config.label.slice(0, -1)}`}
            </DialogTitle>
            <DialogDescription>Fill in the fields below and save.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {config.fields.map((field) => (
              <div key={field.name}>
                {field.type === 'checkbox' ? (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form[field.name]}
                      onChange={(e) => setForm({ ...form, [field.name]: e.target.checked })}
                      data-testid={`${config.key}-${field.name}-input`}
                    />
                    <span className="text-sm">{field.label}</span>
                  </label>
                ) : (
                  <>
                    <Label htmlFor={`${config.key}-${field.name}`}>{field.label}</Label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        id={`${config.key}-${field.name}`}
                        value={form[field.name] ?? ''}
                        onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                        required={field.required}
                        rows={field.rows || 3}
                        className={`rounded-sm ${field.mono ? 'font-mono text-sm' : ''}`}
                        data-testid={`${config.key}-${field.name}-input`}
                      />
                    ) : (
                      <Input
                        id={`${config.key}-${field.name}`}
                        type={field.type === 'number' ? 'number' : 'text'}
                        step={field.float ? 'any' : undefined}
                        value={form[field.name] ?? ''}
                        onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                        required={field.required}
                        className="rounded-sm"
                        data-testid={`${config.key}-${field.name}-input`}
                      />
                    )}
                  </>
                )}
              </div>
            ))}
            <Button type="submit" disabled={saving} className="rounded-sm w-full" data-testid={`${config.key}-save-btn`}>
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
